'use client';

import { useAppState } from './AppStateContext';
import {
  clearGlobalChatRoute,
  globalChatRoute,
  globalChatThreadRoute,
  type GlobalChatMessage,
} from '@/app/api/global-chat/route.def';
import { ClearChatButton } from '@/components/chat/ClearChatButton';
import { SendStopButton } from '@/components/chat/SendStopButton';
import { ThreadView } from '@/components/chat/ThreadView';
import { threadItemsFromChatMsgs, type ThreadItem } from '@/components/chat/thread-model';
import { useMatildaThread } from '@/components/chat/useMatildaThread';
import { useSubagentTerminalWatch } from '@/components/chat/useSubagentTerminalWatch';
import { Kbd } from '@/components/common/Kbd';
import { callApi, streamApi } from '@/domains/common/api-client';
import { useUserPreferences } from '@/domains/common/user-preferences';
import type { ChatAction, ChatMsg } from '@/domains/matilda-agent/chat';
import { trackClientEvent } from '@/external/posthog/client';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const MATILDA_NAME = 'Matilda';

const SEED_ITEMS: ThreadItem[] = [
  {
    kind: 'turn',
    id: 'seed-0',
    agent: MATILDA_NAME,
    time: 'just now',
    segments: [{ kind: 'text', id: 'seed-0-text', body: 'How can I help?' }],
    attach: null,
    chips: [],
    streaming: false,
    stopped: false,
    liveStatus: null,
  },
];

function threadItemsFromStored(messages: GlobalChatMessage[]): ThreadItem[] {
  if (messages.length === 0) return SEED_ITEMS;
  const msgs: ChatMsg[] = messages.map(m =>
    m.role === 'user'
      ? { kind: 'user' as const, time: 'sent', body: m.body }
      : {
          kind: 'agent' as const,
          // The column stores the agent handle ('MATILDA'); captions want
          // the display name.
          agent: MATILDA_NAME,
          time: 'sent',
          body: m.body,
          actions: Array.isArray(m.actions) ? (m.actions as ChatAction[]) : undefined,
        },
  );
  return threadItemsFromChatMsgs(msgs);
}

export function GlobalChatPanel() {
  const [initialItems, setInitialItems] = useState<ThreadItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setInitialItems(null);
    void callApi(globalChatThreadRoute, {
      query: {},
      gracefulFallback: () => {
        if (!cancelled) setInitialItems(SEED_ITEMS);
      },
    }).then(data => {
      if (!cancelled) setInitialItems(threadItemsFromStored(data.messages));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return initialItems ? <GlobalChatPanelInner initialItems={initialItems} /> : null;
}

function GlobalChatPanelInner({ initialItems }: { initialItems: ThreadItem[] }) {
  const router = useRouter();
  const { getPreference } = useUserPreferences();
  const pathname = usePathname();
  const {
    chatOpen: open,
    setChatOpen: setOpen,
    screenContext,
    chatPrefill,
    setChatPrefill,
    chatSeed,
    setChatSeed,
  } = useAppState();
  const [text, setText] = useState('');
  const [clearing, setClearing] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const watchRef = useRef<((id: string) => void) | null>(null);
  const matilda = useMatildaThread({
    agentName: MATILDA_NAME,
    initialItems: () => initialItems,
    echoConfirm: true,
    onRefresh: () => router.refresh(),
    onSubagentSpawned: id => watchRef.current?.(id),
    openStream: (body, gracefulFallback) =>
      streamApi(globalChatRoute, {
        body: {
          body,
          auto_confirm: getPreference('auto_confirm'),
          current_page: pathname ?? undefined,
          screen_context: screenContext ?? undefined,
        },
        gracefulFallback,
      }),
  });
  const { send } = matilda;
  watchRef.current = useSubagentTerminalWatch({
    appendNote: matilda.appendNote,
    clearNotes: matilda.clearNotes,
  });

  useEffect(() => {
    streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight });
  }, [matilda.items, open]);

  // Any view can prime the chat textarea via app state (suggestion cards,
  // timeline tasks, the tour). Apply it — unless the user already has a draft
  // typed, which the prefill must not clobber — then clear it.
  useEffect(() => {
    if (chatPrefill === null) return;
    const value = chatPrefill;
    setOpen(true);
    setText(prev => (prev.trim() ? prev : value));
    setChatPrefill(null);
    // Microtask so the panel mounts (open=true) before we touch the
    // textarea — without it the ref is still null on first call.
    queueMicrotask(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.style.height = 'auto';
      ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
      ta.setSelectionRange(ta.value.length, ta.value.length);
    });
  }, [chatPrefill, setChatPrefill, setOpen]);

  // A view can have Matilda speak first (the "Tell Matilda what to do"
  // hand-off): she posts a framing line and clickable options, each of which
  // sends as the user's reply when clicked. Apply once, then clear.
  useEffect(() => {
    if (chatSeed === null) return;
    const seed = chatSeed;
    setOpen(true);
    setChatSeed(null);
    queueMicrotask(() => {
      matilda.appendNote({
        agent: MATILDA_NAME,
        tag: 'work-item-options',
        body: (
          <div className="flex flex-col gap-2">
            <span>I&rsquo;m looking at {seed.context}. Would you like me to:</span>
            {seed.options.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {seed.options.map((option, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(option)}
                    className="text-left rounded-[8px] border border-line-2 bg-bg-3 px-2.5 py-1.5 text-[12.5px] text-ink hover:border-acc hover:text-acc cursor-pointer transition-colors"
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : null}
            <span className="text-ink-4 text-[11.5px]">…or just tell me something else entirely.</span>
          </div>
        ),
      });
    });
  }, [chatSeed, setChatSeed, setOpen]);

  // TODO(COG-116 follow-up): `matilda:voice` is still a window event — see
  // the OOS ticket for migrating the remaining window events to app state.
  useEffect(() => {
    function onVoice(e: Event) {
      const detail = (e as CustomEvent<{ transcript: string }>).detail;
      if (!detail?.transcript) return;
      setOpen(true);
      send(detail.transcript);
    }
    window.addEventListener('matilda:voice', onVoice);
    return () => window.removeEventListener('matilda:voice', onVoice);
  }, []);

  function sendFromInput(): void {
    const value = text.trim();
    if (!value) return;
    send(value);
    trackClientEvent('chat_message_sent', { surface: 'global' });
    setText('');
    if (taRef.current) taRef.current.style.height = 'auto';
  }

  async function clearChat(): Promise<void> {
    if (matilda.streaming || clearing) return;
    setClearing(true);
    try {
      await callApi(clearGlobalChatRoute, { body: {} });
      matilda.reset(SEED_ITEMS);
    } catch {
      matilda.appendNote({
        agent: 'System',
        body: 'Could not clear chat. Try again in a moment.',
      });
    } finally {
      setClearing(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendFromInput();
    }
  }

  function onInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
  }

  if (!isGeneralRoute(pathname)) return null;

  return open ? (
    <aside className="fixed bottom-[22px] right-[22px] z-30 w-[380px] h-[min(560px,calc(100vh-44px))] bg-bg-2 border border-line-2 rounded-3xl shadow-[0_12px_32px_rgba(0,0,0,0.16)] flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3.5 py-3 pb-1.5">
        <h4 className="m-0 flex items-center gap-1.75 font-sans text-[13px] font-semibold text-ink">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          {MATILDA_NAME}
        </h4>
        <div className="flex items-center gap-1">
          <ClearChatButton
            clearing={clearing}
            disabled={matilda.streaming}
            onClick={() => void clearChat()}
          />
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] border border-transparent bg-transparent text-ink-3 transition-colors hover:border-line-2 hover:bg-bg-hover hover:text-ink"
            onClick={() => setOpen(false)}
            aria-label="Minimize chat"
            title="Minimize chat"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3.5 py-3 flex flex-col gap-3.5" ref={streamRef}>
        <ThreadView
          items={matilda.items}
          variant="floating"
          streaming={matilda.streaming}
          onConfirm={(turnId, segmentId, recommendation) =>
            void matilda.confirm(turnId, segmentId, recommendation)
          }
        />
      </div>

      <div className="border-t border-line bg-transparent px-3 pb-3 pt-2.5 flex-shrink-0">
        <div className="border border-line-2 rounded-[8px] bg-bg-2 px-2.5 pb-1.5 pt-2 transition-all focus-within:border-acc focus-within:shadow-[0_0_0_3px_var(--acc-soft)]">
          <textarea
            className="w-full border-none outline-none bg-transparent text-[12.5px] leading-[1.5] text-ink resize-none font-sans min-h-[36px] max-h-[100px]"
            placeholder="Ask Matilda about your sponsors…"
            rows={1}
            ref={taRef}
            value={text}
            onChange={onInput}
            onKeyDown={onKey}
          />
          <div className="flex justify-between items-center mt-1 gap-1.5">
            <span className="text-[10.5px] text-ink-4">
              {text.length === 0 ? (
                <>
                  <Kbd variant="hint">↵</Kbd> send · <Kbd variant="hint">⌘</Kbd>
                  <Kbd variant="hint">⇧</Kbd>
                  <Kbd variant="hint">M</Kbd> record
                </>
              ) : null}
            </span>
            <SendStopButton
              streaming={matilda.streaming}
              disabled={!matilda.streaming && !text.trim()}
              onClick={() => (matilda.streaming ? matilda.stop() : sendFromInput())}
            />
          </div>
        </div>
      </div>
    </aside>
  ) : null;
}

function isGeneralRoute(pathname: string | null): boolean {
  if (!pathname) return true;
  if (pathname.startsWith('/sponsor/')) return false;
  if (pathname.startsWith('/onboarding/client/')) return false;
  if (pathname.startsWith('/ftw/recording/')) return false;
  if (pathname.startsWith('/computer-use/recording/')) return false;
  return true;
}
